(() => {
  const rootSelector = "[data-keepr-home-promo]";
  const videoSelector = "video[data-keepr-promo-animation]";
  const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  const getMode = () =>
    document.documentElement.getAttribute("data-theme-scheme") === "dark"
      ? "dark"
      : "light";

  const initialize = (root) => {
    if (root.hasAttribute("data-keepr-promo-ready")) return;

    const video = root.querySelector(videoSelector);
    if (!video) return;

    root.setAttribute("data-keepr-promo-ready", "true");
    let isNearViewport = false;
    let currentMode = null;
    let playbackTime = 0;
    let sourceToken = 0;
    const sources = {
      light: {
        animation: video.getAttribute("data-light-animation"),
        poster: video.getAttribute("data-light-poster"),
      },
      dark: {
        animation: video.getAttribute("data-dark-animation"),
        poster: video.getAttribute("data-dark-poster"),
      },
    };

    const playVideo = () => {
      if (motionQuery.matches || !isNearViewport) {
        video.pause();
        video.setAttribute("data-media-state", "poster");
        return;
      }

      video.setAttribute("data-media-state", "playing");
      const playPromise = video.play();
      playPromise?.catch?.(() => {
        video.setAttribute("data-media-state", "paused");
      });
    };

    const unloadVideo = () => {
      sourceToken += 1;
      currentMode = null;
      if (Number.isFinite(video.currentTime) && video.currentTime > 0) {
        playbackTime = video.currentTime;
      }
      video.pause();
      if (video.getAttribute("src")) {
        video.removeAttribute("src");
        video.load();
      }
      video.setAttribute("data-media-state", "poster");
    };

    const updateMedia = () => {
      const mode = getMode();
      const source = sources[mode];
      if (!source) return;

      if (source.poster && video.getAttribute("poster") !== source.poster) {
        video.setAttribute("poster", source.poster);
      }

      if (!isNearViewport || motionQuery.matches) {
        unloadVideo();
        return;
      }

      if (!source.animation) return;
      if (currentMode === mode && video.getAttribute("src") === source.animation) {
        playVideo();
        return;
      }

      const resumeAt = video.getAttribute("src") && Number.isFinite(video.currentTime)
        ? video.currentTime
        : playbackTime;
      playbackTime = resumeAt;
      const token = ++sourceToken;
      currentMode = mode;
      video.pause();
      video.setAttribute("src", source.animation);
      video.load();

      const resume = () => {
        if (token !== sourceToken) return;
        if (resumeAt > 0 && Number.isFinite(video.duration) && video.duration > 0) {
          video.currentTime = Math.min(resumeAt, Math.max(0, video.duration - 0.05));
        }
        playVideo();
      };

      if (video.readyState >= 1) {
        resume();
      } else {
        video.addEventListener("loadedmetadata", resume, { once: true });
      }
    };

    const themeObserver = new MutationObserver(updateMedia);
    themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme-scheme"],
    });

    let viewportObserver;
    if ("IntersectionObserver" in window) {
      viewportObserver = new IntersectionObserver(
        ([entry]) => {
          isNearViewport = entry.isIntersecting;
          updateMedia();
        },
        { rootMargin: "200px 0px" },
      );
      viewportObserver.observe(root);
    } else {
      isNearViewport = true;
    }

    const handleMotionChange = () => {
      updateMedia();
    };
    if (motionQuery.addEventListener) {
      motionQuery.addEventListener("change", handleMotionChange);
    } else {
      motionQuery.addListener(handleMotionChange);
    }

    root.keeprPromoDestroy = () => {
      viewportObserver?.disconnect();
      themeObserver.disconnect();
      if (motionQuery.removeEventListener) {
        motionQuery.removeEventListener("change", handleMotionChange);
      } else {
        motionQuery.removeListener(handleMotionChange);
      }
      unloadVideo();
    };

    updateMedia();
  };

  const initializeWithin = (scope) => {
    if (scope.matches?.(rootSelector)) initialize(scope);
    scope.querySelectorAll?.(rootSelector).forEach(initialize);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeWithin(document));
  } else {
    initializeWithin(document);
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeWithin(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    event.target.querySelector(rootSelector)?.keeprPromoDestroy?.();
  });
})();
